/**
 * Reload the page when analytics consent is withdrawn after a third-party
 * pixel script was injected (#2497).
 *
 * A vendor script that has run cannot be unloaded. Reddit's pixel.js,
 * LinkedIn's insight.min.js and X's uwt.js document no consent or revoke
 * command, and each keeps sending through paths that never touch its global
 * (history hooks, click and dwell listeners, queue drains, URL triggers;
 * review on #2498). A reload is the one mechanism that removes them. The
 * stored choice is already non-granted when this runs, so nothing loads
 * after the reload, and on a reset the banner opens again.
 *
 * Generic on purpose: any loader that inserts a third-party script calls
 * `markThirdPartyScriptInjected()` at insertion time, so a script that is
 * still downloading is covered too. One listener, so several pixels cause a
 * single reload. A withdrawal in another tab arrives as a `storage` event
 * and reloads this tab the same way.
 */

import {
  ANALYTICS_CONSENT_STORAGE_KEY,
  getAnalyticsConsent,
  reopenCookieChoices,
  subscribeAnalyticsConsent,
} from './analyticsConsent';

export const COOKIE_CHOICES_RELOAD_WARNING = 'Changing your cookie choices reloads this page. Anything you haven\'t saved here will be lost.';

const defaultReload = () => window.location.reload();

let scriptInjected = false;
let reloadRequested = false;
let listenerInstalled = false;
let reloadPage = defaultReload;

function onConsentChange(choice) {
  if (choice === 'granted' || !scriptInjected || reloadRequested) return;
  // Decide on what the next boot will read. A swallowed storage write
  // failure leaves 'granted' stored, and reloading would load every pixel
  // again while the withdrawal is lost.
  if (getAnalyticsConsent() === 'granted') return;
  reloadRequested = true;
  reloadPage();
}

/** A withdrawal in another tab only reaches this one as a storage event. */
function onStorage(event) {
  if (event.key === ANALYTICS_CONSENT_STORAGE_KEY || event.key === null) {
    onConsentChange(getAnalyticsConsent());
  }
}

/** Call when this document inserts a third-party tracking script. */
export function markThirdPartyScriptInjected() {
  if (typeof window === 'undefined') return;
  scriptInjected = true;
  if (!listenerInstalled) {
    listenerInstalled = true;
    subscribeAnalyticsConsent(onConsentChange);
    window.addEventListener('storage', onStorage);
  }
}

export function hasThirdPartyScriptInjected() {
  return scriptInjected;
}

/**
 * True only when the visitor explicitly chose Cancel. Fails toward privacy:
 * a missing or throwing `window.confirm`, or any result other than a strict
 * `false`, lets the withdrawal through.
 *
 * Known limit: a suppressed dialog (sandboxed frame without allow-modals,
 * a background frame, a WebView with no confirm handler) also returns
 * `false` and cannot be told apart from a real Cancel. No timing heuristic
 * is used to guess; the visitor sees no banner and can retry.
 */
function visitorCancelledReload() {
  try {
    if (typeof window.confirm !== 'function') return false;
    return window.confirm(COOKIE_CHOICES_RELOAD_WARNING) === false;
  } catch (_) {
    return false;
  }
}

/**
 * "Cookie choices" control. Once a pixel script was injected, the reset
 * reloads the page, so the visitor is warned first. Cancel keeps the stored
 * choice and the page as they are. Returns whether the choices reopened.
 * Every "Cookie choices" entry point must call this, never
 * reopenCookieChoices directly (enforced by a ban test).
 */
export function reopenCookieChoicesWithReloadWarning() {
  if (hasThirdPartyScriptInjected() && !reloadRequested && visitorCancelledReload()) {
    return false;
  }
  reopenCookieChoices();
  return true;
}

export function setPageReloaderForTests(fn) {
  reloadPage = fn || defaultReload;
}

export function resetConsentWithdrawalReloadForTests() {
  scriptInjected = false;
  reloadRequested = false;
  reloadPage = defaultReload;
}
